import { checkIsNoteValid } from '@helios/validators'
import { Break, CodeAvailability } from '../model/break.model'

export interface BreakDialogValidation {
  /** True when Save must be blocked. */
  submitDisabled: boolean
  /** Ordered, human-readable blockers; reasons[0] is shown in the disabled-Save tooltip. */
  reasons: string[]
  /** An ACTION code must be selected (rule a). */
  actionCodeRequired: boolean
  /** A DESCRIPTION code must be selected (rule a). */
  descriptionCodeRequired: boolean
  /** A valid 3-word note is required (rule b or c). */
  noteRequired: boolean
}

const NONE: BreakDialogValidation = {
  submitDisabled: false,
  reasons: [],
  actionCodeRequired: false,
  descriptionCodeRequired: false,
  noteRequired: false,
}

export function computeBreakDialogValidation(input: {
  availability: CodeAvailability
  selectedActionCode: string | null
  selectedDescriptionCode: string | null
  note: string
  /** True when every break in the selection already carries a note (presence only —
   *  notes need not match or be individually 3-word-valid). Leaving the note field
   *  blank is then a no-op that preserves each break's own note, so it relaxes
   *  (never adds to) the note requirement. Defaults to false so existing callers
   *  (the single-break dialog) are unaffected. */
  allBreaksAlreadyNoted?: boolean
  /** Whether a save would write the chosen ACTION code to any break — false when Keep
   *  preserves an existing one on every break. Defaults to true, leaving the
   *  single-break dialog unaffected. */
  actionWritable?: boolean
  /** As actionWritable, for the DESCRIPTION field. */
  descriptionWritable?: boolean
  /** A notes-required action code that Keep will PRESERVE on some break. It still
   *  demands a note even though the user never chose it and its select may be
   *  hidden. Defaults to false. */
  preservedNotesRequired?: boolean
}): BreakDialogValidation {
  const {
    availability,
    selectedActionCode,
    selectedDescriptionCode,
    note,
    allBreaksAlreadyNoted = false,
    actionWritable = true,
    descriptionWritable = true,
    preservedNotesRequired = false,
  } = input

  if (availability.status === 'unavailable') {
    return {
      ...NONE,
      submitDisabled: true,
      reasons: ['Couldn’t load break codes — reopen the dialog to retry.'],
    }
  }
  if (availability.status === 'mixedCodeSets') {
    return NONE
  }

  const hasActionCodes = availability.actionCodes.length > 0
  const hasDescriptionCodes = availability.descriptionCodes.length > 0
  const noCodesConfigured = !hasActionCodes && !hasDescriptionCodes
  // A code is required only where it is both configured and actually written — a
  // hidden select must never be a required one.
  const actionCodeRequired = hasActionCodes && actionWritable
  const descriptionCodeRequired = hasDescriptionCodes && descriptionWritable

  const selectedAction = availability.actionCodes.find(
    c => c.code === selectedActionCode,
  )
  // A notes-required action code demands a note when it is WRITTEN — chosen for a
  // writable field, or preserved by Keep on a break the user never touched. The
  // actionWritable guard matters because a code can sit in the selection (prefilled,
  // or pushed there by quick-apply) for a field no save will write.
  const notesRequiredCode =
    (actionWritable && selectedAction?.notesRequired === true) ||
    preservedNotesRequired
  // Relaxed when every break already carries a note — a blank note is then a no-op
  // that preserves each break's own.
  const noteRequired =
    (notesRequiredCode || noCodesConfigured) && !allBreaksAlreadyNoted

  const reasons: string[] = []
  if (actionCodeRequired && !selectedActionCode) {
    reasons.push('Select an action code.')
  }
  if (descriptionCodeRequired && !selectedDescriptionCode) {
    reasons.push('Select a description code.')
  }
  if (noteRequired && !checkIsNoteValid(note)) {
    reasons.push(
      notesRequiredCode
        ? 'This action code requires a note (3 words minimum).'
        : 'A note (3 words minimum) is required.',
    )
  }

  return {
    submitDisabled: reasons.length > 0,
    reasons,
    actionCodeRequired,
    descriptionCodeRequired,
    noteRequired,
  }
}

/** Sorted action-code strings for the select; [] unless codes are ready. */
export function actionCodeOptions(availability: CodeAvailability): string[] {
  return availability.status === 'ready'
    ? availability.actionCodes
        .map(c => c.code)
        .sort((a, b) => a.localeCompare(b))
    : []
}

/** Sorted description-code strings for the select; [] unless codes are ready. */
export function descriptionCodeOptions(
  availability: CodeAvailability,
): string[] {
  return availability.status === 'ready'
    ? availability.descriptionCodes
        .map(c => c.code)
        .sort((a, b) => a.localeCompare(b))
    : []
}

/**
 * Validation for the single-break Hold/Accept dialog. A bulk selection
 * (breaks.length !== 1) gets no constraints, matching today's behavior.
 */
export function breakDialogValidation(input: {
  breaks: Break[]
  availability: CodeAvailability
  selectedActionCode: string | null
  selectedDescriptionCode: string | null
  note: string
}): BreakDialogValidation {
  if (input.breaks.length !== 1) {
    return { ...NONE }
  }
  return computeBreakDialogValidation({
    availability: input.availability,
    selectedActionCode: input.selectedActionCode,
    selectedDescriptionCode: input.selectedDescriptionCode,
    note: input.note,
  })
}

/** Inline hints for a single break whose saved code is no longer configured. */
export function computeStaleCodeHints(
  breaks: Break[],
  availability: CodeAvailability,
): string[] {
  if (breaks.length !== 1 || availability.status !== 'ready') return []
  const hints: string[] = []
  const b = breaks[0]
  if (
    b.actionCode &&
    !availability.actionCodes.some(c => c.code === b.actionCode)
  ) {
    hints.push(`Saved action code "${b.actionCode}" is no longer configured.`)
  }
  if (
    b.descriptionCode &&
    !availability.descriptionCodes.some(c => c.code === b.descriptionCode)
  ) {
    hints.push(
      `Saved description code "${b.descriptionCode}" is no longer configured.`,
    )
  }
  return hints
}

/** Set of action codes (by code string) whose notesRequired is true. */
export function notesRequiredActionCodes(
  availability: CodeAvailability,
): Set<string> {
  return new Set(
    availability.status === 'ready'
      ? availability.actionCodes.filter(c => c.notesRequired).map(c => c.code)
      : [],
  )
}

/**
 * The saved code to pre-select: the single distinct code across the breaks,
 * but only when it is still among the configured options. Otherwise undefined.
 */
export function prefillSelection(
  savedValues: (string | undefined | null)[],
  options: string[],
): string | undefined {
  const unique = [...new Set(savedValues.filter(Boolean))] as string[]
  return unique.length === 1 && options.includes(unique[0])
    ? unique[0]
    : undefined
}
